import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, LayoutDashboard, Briefcase, Users, CalendarClock } from "lucide-react";
import { RecruitOverview } from "@/components/admin/recrutement/RecruitOverview";
import { OffersTab } from "@/components/admin/recrutement/OffersTab";
import { CandidatesTab } from "@/components/admin/recrutement/CandidatesTab";
import { InterviewsTab } from "@/components/admin/recrutement/InterviewsTab";
import { CandidateDrawer } from "@/components/admin/recrutement/CandidateDrawer";

const TABS = [
  { key: "overview", label: "Tableau de bord", icon: LayoutDashboard },
  { key: "offers", label: "Offres", icon: Briefcase },
  { key: "candidates", label: "Candidats", icon: Users },
  { key: "interviews", label: "Entretiens", icon: CalendarClock },
];

export default function AdminRecrutement() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "overview");
  const [overview, setOverview] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [offers, setOffers] = useState([]);
  const [offerFilter, setOfferFilter] = useState("all");
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const candidateId = params.get("candidate");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: ov }, { data: cat }, { data: of }] = await Promise.all([api.get("/admin/recrutement/overview"), api.get("/admin/recrutement/catalog"), api.get("/admin/recrutement/offers")]);
      setOverview(ov); setCatalog(cat); setOffers(of.items);
      setRefreshKey((k) => k + 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCandidate = (id) => { params.set("candidate", id); setParams(params, { replace: true }); };
  const closeCandidate = () => { params.delete("candidate"); setParams(params, { replace: true }); };
  const showCandidates = (offerId) => { setOfferFilter(offerId); setTab("candidates"); };

  return (
    <div className="min-h-full" data-testid="admin-recrutement">
      <PageHeader eyebrow="Admin · PAG JOB" title="Recrutement & Business Partners"
                  sub="Offres d'emploi et mandats Business Partner publiés sur la page Carrières, pipeline des candidatures, entretiens et grilles d'évaluation."
                  right={<Button variant="outline" onClick={load} disabled={loading} className="h-11" data-testid="recruit-refresh"><RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />Recharger</Button>} />
      <div className="p-6 lg:p-10 space-y-6">
        <div className="flex flex-wrap gap-1.5" role="tablist">
          {TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)} data-testid={`recruit-tab-${t.key}`}
                    className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold px-3 py-2 border transition ${tab === t.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#DEE2E6] hover:border-[#0A0A0A]"}`}>
              <t.icon className="h-3.5 w-3.5" />{t.label}
              {t.key === "candidates" && overview?.candidates?.by_status?.new > 0 && <span className="ml-1 bg-[#002FA7] text-white text-[9px] px-1.5 py-0.5" data-testid="recruit-new-badge">{overview.candidates.by_status.new}</span>}
            </button>
          ))}
        </div>
        {tab === "overview" && <RecruitOverview data={overview} onOpenCandidate={openCandidate} />}
        {tab === "offers" && <OffersTab catalog={catalog} onChanged={load} onShowCandidates={showCandidates} />}
        {tab === "candidates" && <CandidatesTab offers={offers} offerFilter={offerFilter} onOfferFilter={setOfferFilter} onOpen={openCandidate} refreshKey={refreshKey} />}
        {tab === "interviews" && <InterviewsTab catalog={catalog} onOpenCandidate={openCandidate} refreshKey={refreshKey} />}
      </div>
      <CandidateDrawer candidateId={candidateId} onClose={closeCandidate} catalog={catalog} onChanged={load} />
    </div>
  );
}
